import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim } from 'lodash'
import Selection from 'components/molecules/Selection'

const AddNoteModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    notes: '',
    notesAr: '',
    notesFor: '',
  })

  const notesForOptions = [
    { id: 'admin', name: 'Admin' },
    { id: 'user', name: 'User' },
    { id: 'office', name: 'Office' },
    { id: 'all', name: 'All' },
  ]

  useEffect(() => {
    if (id) {
      fetchNoteData()
    }
  }, [id])

  const fetchNoteData = async () => {
    try {
      const response = await apiGet(`notes/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          notes: data.notes || '',
          notesAr: data.notesAr || '',
          notesFor: data.notesFor || '',
        })
      } else {
        notify.error(t('addNoteModal.messages.failedToFetchNoteData'))
      }
    } catch (error) {
      notify.error(t('addNoteModal.messages.failedToFetchNoteData'))
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleNotesForChange = selectedValue => {
    const newNotesFor = selectedValue?.id || ''
    setFormData(prevData => ({
      ...prevData,
      notesFor: newNotesFor,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.notes)) {
      notify.error(t('addNoteModal.messages.notesRequired'))
      return
    }
    if (!trim(formData.notesAr)) {
      notify.error(t('addNoteModal.messages.notesArRequired'))
      return
    }
    if (!trim(formData.notesFor)) {
      notify.error(t('addNoteModal.messages.notesForRequired'))
      return
    }

    try {
      let response
      if (id) {
        response = await apiPut(`notes/${id}`, formData)
        if (response.success) {
          notify.success(t('addNoteModal.messages.noteUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('notes', formData)
        if (response.success) {
          notify.success(t('addNoteModal.messages.noteAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) {
          customClose()
        }
        close()
      } else {
        notify.error(response.message || t('addNoteModal.messages.operationFailed'))
      }
    } catch (error) {
      notify.error(t('addNoteModal.messages.failedToSaveNote'))
    }
  }

  const handleCancel = () => {
    if (customClose) {
      customClose()
    }
    close()
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addNoteModal.labels.notes')}
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={4}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addNoteModal.placeholders.enterNotes')}
        />
      </div>
      <div>
        <label htmlFor="notesAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addNoteModal.labels.notesAr')}
        </label>
        <textarea
          name="notesAr"
          value={formData.notesAr}
          onChange={handleChange}
          rows={4}
          dir="rtl"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-right"
          placeholder={t('addNoteModal.placeholders.enterNotesAr')}
        />
      </div>

      <div>
        <label htmlFor="notesFor" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addNoteModal.labels.notesFor')}
        </label>
        <Selection
          onChange={handleNotesForChange}
          title={t('addNoteModal.placeholders.selectNotesFor')}
          multiple={false}
          list={notesForOptions}
          value={formData.notesFor}
          className="w-full"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addNoteModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addNoteModal.buttons.updateNote') : t('addNoteModal.buttons.addNote')}
        </button>
      </div>
    </div>
  )
}

export default AddNoteModal
